import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { HelpersService } from './helpers.service';
import * as logout from '../../features/auth/actions/logout.actions';
import { ILogoutRequest } from '../../features/auth/models/logout.model';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private isHandling401 = false; // Prevent multiple simultaneous 401 handlers

  constructor(
    private store: Store,
    private router: Router,
    private helperService: HelpersService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check for 401 error
        const is401Error = error.status === 401 ||
                          error?.error?.status === 401 ||
                          (error?.error?.error && error.error.error.StatusCode === 401);

        if (is401Error) {
          // Prevent multiple simultaneous 401 handlers
          if (this.isHandling401) {
            // Set flag in helper service to suppress all other errors
            this.helperService.set401HandlingFlag(true);
            return throwError(() => new Error('401_ALREADY_HANDLED'));
          }

          this.isHandling401 = true;

          // Extract message from error?.error?.Message || error?.error?.message format
          let errorMessage = 'Your session has expired, please log in again.';

          // Try error?.error?.Message || error?.error?.message first (format: {message: 'sample error message'})
          if (error?.error?.message) {
            errorMessage = error?.error?.Message || error?.error?.message;
          }
          // Try error?.error?.Message || error?.error?.message (nested format)
          else if (error?.error?.Message) {
            try {
              const messageStr = error?.error?.Message || error?.error?.message || '';
              // Check if Message is a JSON string that needs parsing
              if (typeof messageStr === 'string' && messageStr.startsWith('{') && messageStr.includes('"message"')) {
                const parsed = JSON.parse(messageStr);
                if (parsed?.message) {
                  errorMessage = parsed.message;
                } else if (messageStr) {
                  errorMessage = messageStr;
                }
              } else if (messageStr) {
                errorMessage = messageStr;
              }
            } catch {
              const messageStr = error?.error?.Message || error?.error?.message;
              if (messageStr) {
                errorMessage = messageStr;
              }
            }
          }
          // Try error.error?.error?.Message || error?.error?.message (lowercase for nested error structures)
          else if (error?.error?.error?.message) {
            errorMessage = error.error?.error?.Message || error?.error?.message;
          }
          // Try error.error?.error?.Message || error?.error?.message (for nested error structures)
          else if (error?.error?.error?.Message) {
            try {
              const messageStr = error?.error?.error?.Message || error?.error?.error?.message || '';
              if (typeof messageStr === 'string' && messageStr.startsWith('{') && messageStr.includes('"message"')) {
                const parsed = JSON.parse(messageStr);
                if (parsed?.message) {
                  errorMessage = parsed.message;
                } else if (messageStr) {
                  errorMessage = messageStr;
                }
              } else if (messageStr) {
                errorMessage = messageStr;
              }
            } catch {
              const messageStr = error?.error?.error?.Message || error?.error?.error?.message;
              if (messageStr) {
                errorMessage = messageStr;
              }
            }
          }

          // Show error notification BEFORE setting the flag to ensure it's not suppressed
          this.helperService.notifyError(errorMessage, 300, 3000);

          // Set flag in helper service to suppress all other errors (after showing the 401 message)
          this.helperService.set401HandlingFlag(true);

          // Dispatch logout
          const token = sessionStorage.getItem('token');
          const req: ILogoutRequest = {
            token: token || '',
            uniqueDeviceId: '00000000-0000-0000-0000-000000000000'
          };
          this.store.dispatch(logout.loadLogoutRequest({ request: req }));

          // Delay navigation slightly to ensure notification is displayed
          setTimeout(() => {
            this.router.navigateByUrl('auth/sign-up');
          }, 100);

          // Reset flags after navigation completes (longer delay to ensure all pending requests are handled)
          setTimeout(() => {
            this.isHandling401 = false;
            this.helperService.set401HandlingFlag(false);
          }, 5000);

          return throwError(() => new Error('401_UNAUTHORIZED_HANDLED'));
        }

        // Pass through non-401 errors
        return throwError(() => error);
      })
    );
  }
}

